import React, { useState, useContext, useEffect, useImperativeHandle, useRef } from "react";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { useForm, Controller } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import { AccountContext } from "context/account";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiSelect from "components/SuiSelect";
import SuiInput from "components/SuiInput";
import SuiButton from "components/SuiButton";
import SuiTagInput from "components/SuiTagInput";

import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import NumbersIcon from "@mui/icons-material/Numbers";

import toast from "react-hot-toast";

import CategoryContext from "context/category-context";

import api from "services/api";
import moment from "moment";

import { sizeChartOptions, seasonOptions } from "utils/data";
import { DatePicker } from "antd";

const sizeChartGenderOptions = sizeChartOptions.map((x) => ({ label: x.label, value: x.label }));
const unitOptions = [
  {
    label: "inch",
    value: "inch",
  },
  {
    label: "cm",
    value: "cm",
  },
];

const CreateSizeChartModal = React.forwardRef(
  (
    {
      visible,
      handleClose,
      handleOk,
      isEdit = false,
      sizeChart = {},
      handleRefresh = () => {},
      handleAfterOk = () => {},
      refreshDelay = 0,
    },
    ref
  ) => {
    const [uploading, setUploading] = useState(false);
    const [selectedGender, setSelectedGender] = useState(sizeChartGenderOptions[0]);

    const fullGenderOption = sizeChartOptions.find((x) => x.value == selectedGender?.value);
    const classOptions = fullGenderOption?.["type"]["class"] || [];
    const categoryOptions = fullGenderOption?.["type"]["category"][0]["children"] || []; //Clothing category

    const {
      register,
      handleSubmit,
      trigger,
      setValue,
      getValues,
      reset,
      watch,
      control,
      formState: { errors },
    } = useForm();
    const navigate = useNavigate();

    useEffect(() => {
      if (isEdit) {
        const gender = sizeChart?.gender
          ? { label: sizeChart.gender, value: sizeChart.gender }
          : undefined;
        let resetPayload = {
          name: sizeChart?.name,
          sizes: sizeChart?.sizes,
          gender: gender,
          class: sizeChart?.class ? { label: sizeChart.class, value: sizeChart.class } : undefined,
          category: sizeChart?.attribute?.category,
          unit: sizeChart?.attribute?.unit,
        };
        setSelectedGender(gender);
        reset(resetPayload);
      } else {
        reset({
          gender: sizeChartGenderOptions[0],
          class: sizeChartOptions?.[0]["type"]["class"][0],
        });
      }
    }, [isEdit]);

    useEffect(() => {
      const subscription = watch((value, { name, type }) => {
        if (name == "gender" && type == "change") {
          const fullGenderOption = sizeChartOptions.find((x) => x.value == value.gender?.value);
          const classOptions = fullGenderOption?.["type"]["class"] || [];
          setValue("class", classOptions?.[0] || undefined);
          setSelectedGender(value.gender);
        }
      });
      return () => subscription.unsubscribe();
    }, [watch]);

    const handleCreateSizeChart = async (data) => {
      try {
        let payload = {
          name: data["name"],
          sizes: data["sizes"],
          gender: data["gender"]?.value,
          class: data["class"]?.value || null,
          attribute: {
            category: data["category"] || null,
            unit: data["unit"],
          },
        };

        if (isEdit) {
          if (!sizeChart.id) {
            return toast.error("Can't find the size chart!");
          }
          let editPayload = {
            name: data["name"],
            gender: data["gender"]?.value,
            class: data["class"]?.value || null,
            attribute: {
              ...sizeChart?.attribute,
              category: data["category"] || null,
              unit: data["unit"] || null,
            },
          };
          const newCreatedSizeChart = await api.sizeChart.update(sizeChart.id, editPayload);
          toast.success(`${data["name"]} has been saved successfully!`);
          handleClose();
          handleAfterOk(newCreatedSizeChart);
          setTimeout(() => {
            handleRefresh();
          }, refreshDelay);
        } else {
          const newCreatedSizeChart = await api.sizeChart.create(payload);
          toast.success(`${data["name"]} has been saved successfully!`);
          handleClose();
          handleAfterOk(newCreatedSizeChart);
          console.log("newCreatedSizeChart", newCreatedSizeChart);
          navigate(`/setup/size-master/${newCreatedSizeChart.data.result.id}`);
        }
      } catch (err) {
        console.log(err);
        toast.error(`Size Chart could not be saved!`);
        setUploading(false);
      }
    };

    return (
      <Dialog
        open={visible}
        onClose={(e, reason) => {
          if (reason != "backdropClick") {
            handleClose();
          }
        }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="md"
        fullWidth={true}
      >
        <Backdrop sx={{ color: "info", zIndex: (theme) => 99999 }} open={uploading}>
          <CircularProgress color="info" sx={{ mr: 1 }} size={32} />
          <SuiTypography variant="h6" fontWeight="medium" color="info">
            Uploading supporting files...
          </SuiTypography>
        </Backdrop>
        <DialogTitle id="alert-dialog-title">
          {isEdit ? "Edit Size Chart" : "Create Size Chart"}

          <IconButton
            aria-label="close"
            onClick={() => handleClose()}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <form
          onSubmit={handleSubmit(handleOk ? handleOk : handleCreateSizeChart)}
          style={{ overflow: "auto", maxHeight: "600px" }}
        >
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              <Grid container spacing={1}>
                <Grid item xs={12} sm={6}>
                  <SuiBox
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                    height="100%"
                  >
                    <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                      <SuiTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        required
                        textTransform="capitalize"
                      >
                        Name
                      </SuiTypography>
                    </SuiBox>
                    <SuiInput
                      placeholder="Name"
                      required
                      inputProps={{ maxLength: 200 }}
                      {...register("name", { required: "A name for size chart is required!" })}
                    />
                  </SuiBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SuiBox
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                    height="100%"
                  >
                    <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                      <SuiTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        textTransform="capitalize"
                      >
                        Unit of Measurement
                      </SuiTypography>
                    </SuiBox>
                    <Controller
                      name="unit"
                      control={control}
                      render={({ field }) => (
                        <SuiSelect
                          {...field}
                          options={unitOptions}
                          placeholder="Unit of Measurement"
                          backspaceRemovesValue
                          isClearable
                        />
                      )}
                    />
                  </SuiBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SuiBox
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                    height="100%"
                  >
                    <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                      <SuiTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        textTransform="capitalize"
                      >
                        Gender
                      </SuiTypography>
                    </SuiBox>
                    <Controller
                      name="gender"
                      control={control}
                      render={({ field }) => (
                        <SuiSelect
                          {...field}
                          options={sizeChartGenderOptions}
                          placeholder="Gender"
                          backspaceRemovesValue
                          value={selectedGender}
                          isClearable
                          onChange={(e) => {
                            setSelectedGender(e);
                            field.onChange(e);
                          }}
                        />
                      )}
                    />
                  </SuiBox>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <SuiBox
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                    height="100%"
                  >
                    <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                      <SuiTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        textTransform="capitalize"
                      >
                        Class
                      </SuiTypography>
                    </SuiBox>
                    <Controller
                      name="class"
                      control={control}
                      render={({ field }) => (
                        <SuiSelect
                          {...field}
                          options={classOptions}
                          placeholder="Size Class"
                          backspaceRemovesValue
                          isClearable
                          isCreatable
                        />
                      )}
                    />
                  </SuiBox>
                </Grid>
                <Grid item xs={12} sm={12}>
                  <SuiBox
                    display="flex"
                    flexDirection="column"
                    justifyContent="flex-end"
                    height="100%"
                  >
                    <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                      <SuiTypography
                        component="label"
                        variant="caption"
                        fontWeight="bold"
                        textTransform="capitalize"
                      >
                        Product Category
                      </SuiTypography>
                    </SuiBox>
                    <Controller
                      name="category"
                      control={control}
                      render={({ field }) => (
                        <SuiSelect
                          {...field}
                          options={categoryOptions}
                          placeholder="Product Category"
                          backspaceRemovesValue
                          isClearable
                          isCreatable
                          isMulti
                          closeMenuOnSelect={false}
                        />
                      )}
                    />
                  </SuiBox>
                </Grid>
                {isEdit ? (
                  <Grid item xs={12} sm={12}>
                    <SuiBox
                      display="flex"
                      flexDirection="column"
                      justifyContent="flex-end"
                      height="100%"
                    >
                      <SuiBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
                        <SuiTypography
                          component="label"
                          variant="caption"
                          fontWeight="bold"
                          textTransform="capitalize"
                        >
                          Sizes
                        </SuiTypography>
                      </SuiBox>
                      <SuiInput
                        placeholder='Sizes with comma delimiter like "XXS,XS,S,M,L,XL,XXL"'
                        disabled={isEdit}
                        inputProps={{ maxLength: 200 }}
                        {...register("sizes", {})}
                      />
                    </SuiBox>
                  </Grid>
                ) : null}
              </Grid>

              <SuiBox
                pt={3}
                px={3}
                display="flex"
                justifyContent="end"
                alignItems={{ xs: "flex-start", sm: "center" }}
                flexDirection={{ xs: "column", sm: "row" }}
              >
                <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
                  <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                    <SuiButton variant="gradient" size="medium" onClick={handleClose} color="light">
                      Cancel
                    </SuiButton>
                  </SuiBox>
                </SuiBox>
                <SuiBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
                  <SuiBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
                    <SuiButton size="medium" variant="gradient" color="dark" type="submit">
                      {isEdit ? "Save" : "Create"}
                    </SuiButton>
                  </SuiBox>
                </SuiBox>
              </SuiBox>
            </DialogContentText>
          </DialogContent>
        </form>
      </Dialog>
    );
  }
);

export default CreateSizeChartModal;
